import {request, UserPayloadBloom, UserPayloadFlava, UserPayloadInstinct, UserPayloadPortal} from 'services';
import {Apps} from 'enums';
import {AdminsRoles} from "enums/AdminsRoles";
import {EnvSetup, getSupportUrl} from "enums/Envs";
import {SupportActions} from "enums/SupportActions";

export interface AvailableApp {
    isTest: boolean
    appId: number
}

interface LoginPayloadInstinct {
    jToken: string;
    rToken: string;
    loggedInUser: UserPayloadInstinct;
    supportUserKey?: string;
    availableApps?: AvailableApp[];
}


interface LoginPayloadBloom {
    jwt: string;
    refreshToken: string;
    user: UserPayloadBloom;
    supportUserKey?: string;
    availableApps?: AvailableApp[];
}

interface LoginPayloadSpark {
    token: string;
    refreshToken: string;
    sparker: UserPayloadPortal;
    supportUserKey?: string;
    availableApps?: AvailableApp[];
}

interface LoginPayloadFlava {
    jwt: string;
    refreshToken: string;
    profile: UserPayloadFlava;
    supportUserKey?: string;
    availableApps?: AvailableApp[];
}

export const getRefreshUrl = (app: Apps) => {
    const appendix = '/support';
    return app === Apps.Flava || app === Apps.Bloom
        ? `sign-in/refresh${appendix}`
        : app === Apps.Instinct ? `login/refresh${appendix}` : app === Apps.Spark ? `enter/refresh${appendix}` : ''
}

const supportLoginAndRefresh = async (app: Apps, url: string, token: string, userId: string) => {
    try {
        if (app === Apps.Instinct) {
            const response = await request<LoginPayloadInstinct>({
                url,
                method: 'POST',
                data: {rToken: token, id: userId},
            });
            return {
                user: response?.loggedInUser ? {
                    id: response?.loggedInUser.id,
                    name: response?.loggedInUser.name,
                    isSuperAdmin: response?.loggedInUser.isSuperAdmin,
                } : undefined,
                token: response?.jToken,
                refresh: response?.rToken,
                supportUserKey: response?.supportUserKey,
                availableApps: response?.availableApps
            };
        } else if (app === Apps.Spark) {
            const response = await request<LoginPayloadSpark>({
                url,
                method: 'POST',
                data: {refreshToken: token, sparkerId: userId},
            });
            return {
                user: response?.sparker ? {
                    id: response?.sparker?.id,
                    name: response?.sparker?.name,
                    isSuperAdmin: response?.sparker.isSuperSupport,
                } : undefined,
                token: response?.token,
                refresh: response?.refreshToken,
                supportUserKey: response?.supportUserKey,
                availableApps: response?.availableApps
            };
        } else {
            const response = await request<LoginPayloadFlava>({
                url,
                method: 'POST',
                data: {refreshToken: token, id: userId},
            });
            return {
                user: response?.profile ? {
                    id: response?.profile?.id,
                    name: response?.profile?.firstname,
                    isSuperAdmin: response?.profile.isSuperAdmin,
                } : undefined,
                token: response?.jwt,
                refresh: response?.refreshToken,
                supportUserKey: response?.supportUserKey,
                availableApps: response?.availableApps
            };
        }
    } catch (err) {
        return null
    }
}

export const loginService = {
    login: async (token: string, app: Apps) => {
        if (app === Apps.Instinct) {
            const response = await request<LoginPayloadInstinct>({
                url: 'login/support',
                method: 'POST',
                data: {accessToken: token, languageCode: 'en'},
            });
            return {
                user: response?.loggedInUser ? {
                    id: response?.loggedInUser.id,
                    name: response?.loggedInUser.name,
                    isSuperAdmin: response?.loggedInUser.isSuperAdmin,
                    supportRole: response?.loggedInUser.supportRole ?? AdminsRoles.Moderator
                } : undefined,
                token: response?.jToken,
                refresh: response?.rToken,
                supportUserKey: response?.supportUserKey,
                availableApps: response?.availableApps
            };
        } else if (app === Apps.Bloom) {
            const response = await request<LoginPayloadBloom>({
                url: 'sign-in/apple',
                method: 'POST',
                data: {token},
            });
            return {
                user: response?.user ? {
                    id: response?.user?.id,
                    name: response?.user?.name,
                    isSuperAdmin: response?.user.isSuperAdmin,
                    supportRole: response?.user.supportRole ?? AdminsRoles.Moderator
                } : undefined,
                token: response?.jwt,
                refresh: response?.refreshToken,
                supportUserKey: response?.supportUserKey,
                availableApps: response?.availableApps
            };
        } else if (app === Apps.Spark) {
            const response = await request<LoginPayloadSpark>({
                url: 'enter/support',
                method: 'POST',
                data: {enterCode: token},
            });
            return {
                user: response?.sparker ? {
                    id: response?.sparker?.id,
                    name: response?.sparker?.name,
                    isSuperAdmin: response?.sparker.isSuperSupport,
                    supportRole: response?.sparker.supportRole ?? AdminsRoles.Moderator
                } : undefined,
                token: response?.token,
                refresh: response?.refreshToken,
                supportUserKey: response?.supportUserKey,
                availableApps: response?.availableApps
            };
        } else {
            const response = await request<LoginPayloadFlava>({
                url: 'sign-in/support',
                method: 'POST',
                data: {accessToken: token, localeCode: 'ru'},
            });
            return {
                user: response?.profile ? {
                    id: response?.profile?.id,
                    name: response?.profile?.firstname,
                    isSuperAdmin: response?.profile.isSuperAdmin,
                    supportRole: response?.profile.supportRole ?? AdminsRoles.Moderator
                } : undefined,
                token: response?.jwt,
                refresh: response?.refreshToken,
                supportUserKey: response?.supportUserKey,
                availableApps: response?.availableApps
            };
        }
    },
    refresh: async (token: string, userId: string, app: Apps, supportUserKey?: string | null) => {
        const url = getRefreshUrl(app)

        if (supportUserKey) {
            return supportLoginAndRefresh(app, url, token, supportUserKey)
        }

        if (app === Apps.Instinct) {
            const response = await request<LoginPayloadInstinct>({
                url: url,
                method: 'POST',
                data: {rToken: token, id: userId},
            });
            return {user: response?.loggedInUser.id, token: response?.jToken, refresh: response?.rToken};
        } else if (app === Apps.Flava) {
            const response = await request<LoginPayloadFlava>({
                url: url,
                method: 'POST',
                data: {refreshToken: token, id: userId},
            });
            return {user: response?.profile.id, token: response?.jwt, refresh: response?.refreshToken};
        } else if (app === Apps.Bloom) {
            const response = await request<LoginPayloadBloom>({
                url: url,
                method: 'POST',
                data: {refreshToken: token, userId},
            });
            return {user: response?.user.id, token: response?.jwt, refresh: response?.refreshToken};
        } else if (app === Apps.Spark) {
            const response = await request<LoginPayloadSpark>({
                url: url,
                method: 'POST',
                data: {refreshToken: token, sparkerId: userId},
            });
            return {user: response?.sparker.id, token: response?.token, refresh: response?.refreshToken};
        } else {
            alert("loginServices.refresh Not implemented!")
        }

    },
    getUser: async (app: Apps) => {
        const url = app === Apps.Instinct || app === Apps.Flava ? 'profile' : app === Apps.Spark ? 'me' : 'user'
        const response = await request<{
            id?: string,
            key?: string,
            firstname?: string,
            nickname?: string,
            name?: string,
            isSuperAdmin: boolean,
            isSuperSupport: boolean,
            supportRole: AdminsRoles
        }>({
            url: url,
            method: 'GET',
        });
        const user = {
            id: response?.id ?? response?.key ?? '',
            name: response?.firstname ?? response?.name ?? response?.nickname ?? '',
            isSuperAdmin: response?.isSuperAdmin ?? response?.isSuperSupport ?? false,
            supportRole: response?.supportRole ?? AdminsRoles.Moderator
        };
        return user.id ? user : undefined;
    },
    getSupportActions: async (env: EnvSetup) => {
        const response = await request<SupportActions[]>({
            method: 'get',
            url: `/${getSupportUrl(env)}/actions`
        });
        return response ?? []
    }
};
